/**
 * SellerReviews - Amazon-style customer reviews block.
 *
 * Collapsed state: compact summary with big rating number, star row, and
 *                  horizontal percentage bars for 5→1 stars.
 * Expanded state:  same summary + "Top reviews from {country}" heading +
 *                  full review cards (verified badge, helpful/report actions)
 *                  + sort dropdown + pagination.
 *
 * Uses Zeilian's blue/indigo/purple theme instead of Amazon's orange.
 */
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Skeleton } from './ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import {
  Star, ThumbsUp, MessageSquare, CheckCircle, Flag, ChevronLeft,
  ChevronRight, User, ChevronDown, ChevronUp, PenSquare,
} from 'lucide-react';
import ReviewDialog from './ReviewDialog';
import ReportDialog from './ReportDialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const COUNTRY_LABELS = {
  UK: 'the United Kingdom', GB: 'the United Kingdom',
  SL: 'Sri Lanka', LK: 'Sri Lanka',
  EU: 'the European Union',
  US: 'the United States',
};

const SellerReviews = ({ sellerId, sellerName, listingId, listingCountry, expanded, onExpandedChange, onSummaryLoaded, hideHeader = false }) => {
  const [reviews, setReviews] = useState([]);
  const [ratingSummary, setRatingSummary] = useState(null);
  const [canReview, setCanReview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [sortBy, setSortBy] = useState('recent');
  const [showReviewDialog, setShowReviewDialog] = useState(false);
  const [reportReviewId, setReportReviewId] = useState(null);
  const [internalExpanded, setInternalExpanded] = useState(false);

  // Controlled if `expanded` prop is provided; the parent can also pass
  // `onExpandedChange` to let the internal chevron toggle upward.
  const isExpanded = expanded !== undefined ? expanded : internalExpanded;
  const toggleExpanded = () => {
    if (onExpandedChange) {
      onExpandedChange(!isExpanded);
    } else {
      setInternalExpanded((v) => !v);
    }
  };

  useEffect(() => {
    if (sellerId) fetchReviews();
    // eslint-disable-next-line
  }, [sellerId, page, sortBy]);

  const fetchReviews = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await fetch(
        `${API_URL}/api/reviews/seller/${sellerId}?page=${page}&limit=5&sort_by=${sortBy}`,
        { headers }
      );
      const data = await response.json();
      if (data.success) {
        setReviews(data.reviews || []);
        setRatingSummary(data.rating_summary);
        setCanReview(data.can_review);
        setTotalPages(data.total_pages || 1);
        // Bubble up to parent so the compact preview elsewhere can render.
        if (onSummaryLoaded && data.rating_summary) {
          onSummaryLoaded(data.rating_summary);
        }
      }
    } catch (error) {
      console.error('Error fetching reviews:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkHelpful = async (reviewId) => {
    const token = localStorage.getItem('token');
    if (!token) {
      alert('Please login to mark reviews as helpful');
      return;
    }
    try {
      const response = await fetch(`${API_URL}/api/reviews/${reviewId}/helpful`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ helpful: true }),
      });
      const data = await response.json();
      if (data.success) {
        setReviews((prev) => prev.map((r) =>
          r.id === reviewId ? { ...r, helpful_count: (r.helpful_count || 0) + 1, marked_helpful: true } : r
        ));
      }
    } catch (error) {
      console.error('Error marking helpful:', error);
    }
  };

  const handleReviewSubmit = () => {
    fetchReviews();
  };

  // ---- Star row ----
  const Stars = ({ rating, size = 'h-4 w-4' }) => (
    <div className="flex items-center" aria-label={`Rated ${rating} out of 5`}>
      {[1, 2, 3, 4, 5].map((s) => (
        <Star
          key={s}
          className={`${size} ${s <= Math.round(rating) ? 'fill-amber-400 text-amber-400' : 'text-gray-300'}`}
        />
      ))}
    </div>
  );

  // ---- Percentage bar row ----
  const PctBar = ({ star, count, total }) => {
    const pct = total > 0 ? Math.round((count / total) * 100) : 0;
    return (
      <div className="flex items-center gap-2 text-xs" data-testid={`rating-bar-${star}`}>
        <span className="w-11 shrink-0 text-gray-600">{star} star</span>
        <div className="flex-1 h-2.5 rounded-full bg-gray-100 overflow-hidden">
          <div
            className="h-full rounded-full bg-gradient-to-r from-indigo-500 to-blue-500 transition-all"
            style={{ width: `${pct}%` }}
          />
        </div>
        <span className="w-8 shrink-0 text-right font-semibold text-gray-700">{pct}%</span>
      </div>
    );
  };

  const total = ratingSummary?.total_reviews || 0;
  const avg = ratingSummary?.average_rating || 0;
  const countryLabel = COUNTRY_LABELS[(listingCountry || '').toUpperCase()] || 'the world';

  // ==== Loading state ====
  if (loading && reviews.length === 0) {
    return (
      <Card data-testid="seller-reviews-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold">Customer reviews</CardTitle>
        </CardHeader>
        <CardContent><Skeleton className="h-24 w-full" /></CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card className="border border-gray-200 shadow-sm" data-testid="seller-reviews-card">
        {/* ---- Header / toggle ---- */}
        <CardHeader className="pb-2">
          <div
            className="flex items-center justify-between cursor-pointer group"
            onClick={toggleExpanded}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleExpanded(); } }}
            data-testid="seller-reviews-toggle"
          >
            <CardTitle className="text-base font-semibold text-gray-900">Customer reviews</CardTitle>
            {isExpanded ? (
              <ChevronUp className="h-4 w-4 text-gray-500 group-hover:text-indigo-600" />
            ) : (
              <ChevronDown className="h-4 w-4 text-gray-500 group-hover:text-indigo-600" />
            )}
          </div>
        </CardHeader>

        <CardContent className="pt-0 space-y-4">
          {/* ---- Summary (always visible) ---- */}
          {total === 0 ? (
            <div className="text-center py-4 text-gray-500" data-testid="seller-reviews-empty">
              <MessageSquare className="h-6 w-6 mx-auto mb-2 text-gray-300" />
              <p className="text-sm">No reviews yet</p>
              {canReview?.can_review && (
                <p className="text-xs mt-1">Be the first to review this seller!</p>
              )}
            </div>
          ) : (
            <div className="space-y-3" data-testid="seller-reviews-summary">
              <div className="flex items-center gap-2">
                <Stars rating={avg} size="h-4 w-4" />
                <span className="text-sm font-semibold text-gray-900">
                  {avg.toFixed(1)} out of 5
                </span>
              </div>
              <p className="text-xs text-gray-500">
                {total.toLocaleString()} global rating{total !== 1 ? 's' : ''}
              </p>
              <div className="space-y-1.5">
                {[5, 4, 3, 2, 1].map((s) => (
                  <PctBar
                    key={s}
                    star={s}
                    count={ratingSummary?.rating_distribution?.[s.toString()] || 0}
                    total={total}
                  />
                ))}
              </div>
            </div>
          )}

          {/* ---- Expanded: top reviews from {country} ---- */}
          {isExpanded && total > 0 && (
            <div className="pt-2 border-t border-gray-200 space-y-4" data-testid="seller-reviews-expanded">
              <div className="flex items-center justify-between gap-2">
                <h4 className="text-sm font-semibold text-gray-900">
                  Top reviews from {countryLabel}
                </h4>
                <Select value={sortBy} onValueChange={setSortBy}>
                  <SelectTrigger className="w-28 h-7 text-xs" data-testid="reviews-sort">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="recent">Most Recent</SelectItem>
                    <SelectItem value="rating_high">Highest</SelectItem>
                    <SelectItem value="rating_low">Lowest</SelectItem>
                    <SelectItem value="helpful">Most Helpful</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-4">
                {reviews.map((review) => (
                  <div key={review.id} className="pb-4 border-b border-gray-100 last:border-b-0 last:pb-0"
                       data-testid={`review-${review.id}`}>
                    {/* Author row */}
                    <div className="flex items-center gap-2 mb-1.5">
                      <div className="w-6 h-6 bg-gradient-to-br from-indigo-100 to-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                        {review.reviewer_image ? (
                          <img src={review.reviewer_image} alt={review.reviewer_name}
                               className="w-6 h-6 rounded-full object-cover" />
                        ) : (
                          <User className="h-3.5 w-3.5 text-indigo-600" />
                        )}
                      </div>
                      <span className="text-xs font-medium text-gray-700">{review.reviewer_name}</span>
                    </div>

                    {/* Stars + title */}
                    <div className="flex items-center gap-2 mb-0.5">
                      <Stars rating={review.rating} size="h-3.5 w-3.5" />
                      {review.title && (
                        <h5 className="font-semibold text-sm text-gray-900 leading-tight">{review.title}</h5>
                      )}
                    </div>

                    {/* Reviewed line */}
                    <div className="flex items-center gap-2 text-[11px] text-gray-500 mb-2">
                      <span>
                        Reviewed in {countryLabel} on{' '}
                        {new Date(review.created_at).toLocaleDateString(undefined, {
                          day: 'numeric', month: 'long', year: 'numeric',
                        })}
                      </span>
                      {review.verified_contact && (
                        <span className="text-emerald-600 font-medium flex items-center gap-0.5">
                          <CheckCircle className="h-3 w-3" />
                          Verified
                        </span>
                      )}
                    </div>

                    {/* Body */}
                    <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-line">
                      {review.content}
                    </p>

                    {/* Helpful footer */}
                    {(review.helpful_count > 0) && (
                      <p className="text-xs text-gray-500 mt-2">
                        {review.helpful_count} {review.helpful_count === 1 ? 'person' : 'people'} found this helpful
                      </p>
                    )}

                    <div className="flex items-center gap-2 mt-2">
                      <Button
                        onClick={() => handleMarkHelpful(review.id)}
                        disabled={review.marked_helpful}
                        size="sm"
                        variant="outline"
                        className={`h-7 text-xs rounded-full ${review.marked_helpful
                          ? 'text-indigo-600 border-indigo-200 bg-indigo-50'
                          : 'border-gray-300 hover:border-indigo-400 hover:text-indigo-600'}`}
                        data-testid={`review-helpful-${review.id}`}
                      >
                        <ThumbsUp className="h-3 w-3 mr-1" />
                        Helpful
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 text-xs text-gray-500 hover:text-red-600"
                        onClick={() => setReportReviewId(review.id)}
                        data-testid={`review-report-${review.id}`}
                      >
                        <Flag className="h-3 w-3 mr-1" />
                        Report
                      </Button>
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 pt-1">
                  <Button variant="outline" size="sm" className="h-7 text-xs"
                          onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}>
                    <ChevronLeft className="h-3 w-3" />
                  </Button>
                  <span className="text-xs text-gray-600">{page} / {totalPages}</span>
                  <Button variant="outline" size="sm" className="h-7 text-xs"
                          onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages}>
                    <ChevronRight className="h-3 w-3" />
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* ---- Write review CTA ---- */}
          {canReview?.can_review && (
            <Button
              onClick={() => setShowReviewDialog(true)}
              size="sm"
              className="w-full bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700 text-white"
              data-testid="write-review-btn"
            >
              <PenSquare className="h-3.5 w-3.5 mr-1.5" />
              Write a review
            </Button>
          )}
        </CardContent>
      </Card>

      <ReviewDialog
        open={showReviewDialog}
        sellerId={sellerId}
        sellerName={sellerName}
        listingId={listingId}
        onClose={() => setShowReviewDialog(false)}
        onSubmit={handleReviewSubmit}
      />

      {reportReviewId && (
        <ReportDialog
          open={!!reportReviewId}
          onClose={() => setReportReviewId(null)}
          type="review"
          targetId={reportReviewId}
          targetName="Review"
        />
      )}
    </>
  );
};

export default SellerReviews;
